import {isNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

/**
 * The route a concrete URL stands for, with record identifiers put back as
 * their parameter names.
 *
 * The values come from the route react-router actually matched, not from a
 * guess about which segments look like an id — so `/vehicles/0f8d…/finances`
 * becomes `/vehicles/{id}/finances` and a genuine `/list/2` is left alone.
 *
 * Without this every vehicle, business case and advert is its own page: the
 * Pages report is 142 896 rows deep for 28 days, GA4 folds the tail into
 * `(other)`, and nobody can say how often a screen is opened.
 */
export function getPageRoute(pathname: string, params: Record<string, string | undefined>): string {
  const nameByValue = new Map(
    Object.entries(params)
      .filter(([, value]) => isNotNilOrEmpty(value))
      .map(([name, value]) => [value as string, name])
  );

  // Segment by segment, and only on a whole segment: an id that also happens to
  // be the start of a longer segment must not turn that segment into a
  // parameter.
  return pathname
    .split('/')
    .map((segment) => {
      const name = nameByValue.get(segment);

      return isNil(name) ? segment : `{${name}}`;
    })
    .join('/');
}

/**
 * The part of the product a screen belongs to — `vehicles`, `sourcing`,
 * `business-case`, `warehouse` and so on.
 *
 * This is the coarse cut most product questions start from, and unlike the page
 * title it is the same in every language. Today the vehicle list is counted
 * twice, 61k views as "Vozidla" and 49k as "Pojazdy", because GA4 groups by a
 * translated title.
 */
export function getPageSection(pathname: string): string {
  // Interests nested below a customer or a vehicle are a distinct product
  // area, not another view of the parent record.
  if (/^\/(?:vehicles|customer)\/[^/]+\/interests(?:\/|$)/.test(pathname)) {
    return 'interests';
  }

  const [section] = pathname.split('/').filter((segment) => segment !== '');

  return section ?? 'home';
}

export function getPageModule(pathname: string): string {
  const section = getPageSection(pathname);

  if (section === 'home') {
    return 'Dashboard';
  }

  // The module is derived from the route itself, rather than maintained as a
  // second registry. A new route is immediately visible in analytics and its
  // label stays independent of the UI language.
  return section
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(/[-_\s]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
